import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import DataTable, {
  DataTableColumnHeader, DataTablePagination
} from '../../ui/DataTable'
import Checkbox from '../../ui/Checkbox'
import Tooltip from '../../ui/Tooltip'
import EmptyState from '../../ui/EmptyState'
import SettingIcon from '../../ui/icons/SettingIcon'
import TriggeredAlertsRow from './TriggeredAlertsRow'
import {
  PRIMARY_COLUMN_KEYS,
  PRIMARY_COLUMN_LABELS,
  PRIMARY_COLUMN_ORDER,
  DEFAULT_COLUMN_PREFERENCES,
  EXTRA_COLUMN_TYPES,
  orderedColumnSlots
} from './constants'

const SETTINGS_BTN_CLASS = 'tw:bg-transparent tw:border-0 tw:cursor-pointer tw:text-gray-600 '
  + 'tw:inline-flex tw:items-center tw:align-middle '
  + 'tw:disabled:opacity-50 tw:disabled:cursor-not-allowed tw:flex-shrink-0'

const EMPTY_MESSAGE = 'Les alertes déclenchées apparaîtront ici une fois les données disponibles.'

// Map column ids (front-end) → backend sort keys. Columns without an entry are not sortable.
const SORT_KEYS = {
  [PRIMARY_COLUMN_KEYS.ID]: 'id',
  // Org-level only: the collect column maps to the backend collects.name sort.
  [PRIMARY_COLUMN_KEYS.COLLECT_NAME]: 'collect_name',
  [PRIMARY_COLUMN_KEYS.ALERT_NAME]: 'alert_name',
  [PRIMARY_COLUMN_KEYS.STATUT]: 'issue_status',
  [PRIMARY_COLUMN_KEYS.CATEGORIE]: 'issue_category',
  [PRIMARY_COLUMN_KEYS.CREATED_AT]: 'created_at',
  [PRIMARY_COLUMN_KEYS.ATTRIBUTION]: 'attribution_name',
  [PRIMARY_COLUMN_KEYS.RESOURCE_NAME]: 'resource_name'
}
const SORT_KEY_BY_BACKEND = Object.fromEntries(
  Object.entries(SORT_KEYS).map(([columnId, backendKey]) => [backendKey, columnId])
)

// A variable column sorts through "variable_<id>", round-tripped from its TanStack column id.
const VARIABLE_COLUMN_RE = /^extra-variable-(?<id>\d+)$/u
const VARIABLE_SORT_RE = /^variable_(?<id>\d+)$/u

const sortKeyForColumn = columnId => {
  const match = VARIABLE_COLUMN_RE.exec(columnId)
  return match ? `variable_${match.groups.id}` : SORT_KEYS[columnId]
}

const columnIdForSort = backendKey => {
  const match = VARIABLE_SORT_RE.exec(backendKey)
  return match ? `extra-variable-${match.groups.id}` : SORT_KEY_BY_BACKEND[backendKey]
}

const DEFAULT_ISSUES = []
const DEFAULT_VISIBLE_COLUMNS = { primary: DEFAULT_COLUMN_PREFERENCES.primaryColumns, extra: [] }
const EMPTY_STATE = <EmptyState message={EMPTY_MESSAGE} title="Aucune donnée" />

const buildPrimaryColumn = key => {
  const sortable = Boolean(SORT_KEYS[key])
  const renderSortable = ({ column }) =>
    <DataTableColumnHeader column={column} title={PRIMARY_COLUMN_LABELS[key]} />
  const renderPlain = () => <span>{PRIMARY_COLUMN_LABELS[key]}</span>
  return {
    id: key,
    accessorKey: key,
    enableSorting: sortable,
    header: sortable ? renderSortable : renderPlain
  }
}

// Extra column titles can be arbitrarily long, so truncate with a Tooltip carrying the full label.
// The type prefix keeps the TanStack id unique when a question and a variable share a numeric id.
const EXTRA_HEADER_CLASS = 'tw:block tw:max-w-40 tw:truncate'

const buildExtraColumn = extra => {
  const sortable = extra.type === EXTRA_COLUMN_TYPES.VARIABLE
  const label = (
    <Tooltip className={EXTRA_HEADER_CLASS} content={extra.title}>
      {extra.title}
    </Tooltip>
  )
  let header = () => label
  if (sortable)
    header = ({ column }) => <DataTableColumnHeader column={column} title={label} />
  // Adding an accessorFn (cells come from TriggeredAlertsRow, not flexRender)
  // Turns this into an accessor column so TanStack honours enableSorting.
  return { id: `extra-${extra.type}-${extra.id}`, accessorFn: () => null, enableSorting: sortable, header }
}

const TriggeredAlertsTable = ({
  issues = DEFAULT_ISSUES,
  pagy,
  loading = false,
  error = null,
  sort = null,
  direction = 'desc',
  isIssueUpdating = null,
  activeIssueId = null,
  selectedIds, allFiltered, onToggleRow, onToggleAll,
  onSortChange, onPageChange, onPerPageChange,
  onIssueClick, onShareClick, onStatusEdit, onAttributionEdit, onColumnSettingsClick,
  onExportPdf, onPhotosClick,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
  columnOrder = PRIMARY_COLUMN_ORDER
}) => {
  const primaryKeys = visibleColumns.primary
  const primaryColumns = useMemo(() => {
    // The ID column is mandatory even if a stored preference omits it.
    const keys = [...primaryKeys]
    if (!keys.includes(PRIMARY_COLUMN_KEYS.ID))
      keys.unshift(PRIMARY_COLUMN_KEYS.ID)
    // The org view supplies its own order (adds collect, absent from PRIMARY_COLUMN_ORDER).
    return columnOrder.filter(key => keys.includes(key))
  }, [primaryKeys, columnOrder])
  const extraColumns = visibleColumns.extra
  const tableColumns = useMemo(() => orderedColumnSlots(primaryColumns, extraColumns).map(slot => {
    if (slot.kind === 'primary')
      return buildPrimaryColumn(slot.key)
    return buildExtraColumn(slot.column)
  }), [primaryColumns, extraColumns])

  const sortingState = useMemo(() => {
    if (!sort)
      return []
    const columnId = columnIdForSort(sort)
    return columnId ? [{ id: columnId, desc: direction === 'desc' }] : []
  }, [sort, direction])

  const handleSortingChange = useCallback(updater => {
    // Guard against spam-clicks while a fetch is in flight (cf. spam-click rule).
    if (loading)
      return
    const next = typeof updater === 'function' ? updater(sortingState) : updater
    if (next.length === 0) {
      onSortChange(null, 'desc')
      return
    }
    const [{ id, desc }] = next
    onSortChange(sortKeyForColumn(id), desc ? 'desc' : 'asc')
  }, [loading, sortingState, onSortChange])

  const visibleIds = useMemo(() => issues.map(issue => issue.id), [issues])
  const everyVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id))
  const allSelected = allFiltered || everyVisibleSelected
  const someSelected = !allSelected && visibleIds.some(id => selectedIds.has(id))
  const handleToggleAll = useCallback(() => onToggleAll(visibleIds), [onToggleAll, visibleIds])
  // Leaving the filtered-scope mode needs the rows on screen, which only the table knows.
  const handleToggleRow = useCallback(
    issueId => onToggleRow(issueId, visibleIds), [onToggleRow, visibleIds]
  )

  const renderSelectHeader = useCallback(() => (
    <Checkbox
      aria-label="Tout sélectionner"
      checked={allSelected}
      disabled={visibleIds.length === 0}
      indeterminate={someSelected}
      onChange={handleToggleAll}
    />
  ), [allSelected, someSelected, visibleIds.length, handleToggleAll])

  const cogButton = (
    <button
      aria-label="Personnaliser les colonnes"
      className={SETTINGS_BTN_CLASS}
      disabled={loading}
      type="button"
      onClick={onColumnSettingsClick}
    >
      <SettingIcon className="tw:w-5 tw:h-5" />
    </button>
  )

  const tableColumnsWithSelect = useMemo(() => [
    { id: '_select', enableSorting: false, header: renderSelectHeader },
    ...tableColumns
  ], [renderSelectHeader, tableColumns])

  const table = useReactTable({
    data: issues,
    columns: tableColumnsWithSelect,
    state: { sorting: sortingState },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    // Numeric columns default to desc-first, which would desync from the parent sort state.
    sortDescFirst: false,
    getRowId: row => String(row.id)
  })


  const renderRow = useCallback(row => (
    <TriggeredAlertsRow
      key={row.id}
      disabled={isIssueUpdating ? isIssueUpdating(row.original.id) : false}
      extraColumns={extraColumns}
      hasOpenPopup={activeIssueId === row.original.id}
      issue={row.original}
      primaryColumns={primaryColumns}
      selected={allFiltered || selectedIds.has(row.original.id)}
      onAttributionEdit={onAttributionEdit}
      onExportPdf={onExportPdf}
      onIssueClick={onIssueClick}
      onPhotosClick={onPhotosClick}
      onShareClick={onShareClick}
      onStatusEdit={onStatusEdit}
      onToggleRow={handleToggleRow}
    />
  ), [
    isIssueUpdating,
    extraColumns,
    activeIssueId,
    primaryColumns,
    selectedIds,
    allFiltered,
    onAttributionEdit,
    onExportPdf,
    onIssueClick,
    onPhotosClick,
    onShareClick,
    onStatusEdit,
    handleToggleRow
  ])

  const pagination = (
    <DataTablePagination
      disabled={loading}
      pagy={pagy}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
    />
  )

  return (
    <DataTable
      actionsHeader={cogButton}
      emptyState={EMPTY_STATE}
      error={error}
      loading={loading}
      pagination={pagination}
      renderRow={renderRow}
      table={table}
    />
  )
}

TriggeredAlertsTable.propTypes = {
  activeIssueId: PropTypes.number,
  allFiltered: PropTypes.bool,
  columnOrder: PropTypes.arrayOf(PropTypes.string),
  direction: PropTypes.oneOf(['asc', 'desc']),
  error: PropTypes.instanceOf(Error),
  isIssueUpdating: PropTypes.func,
  issues: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired })),
  loading: PropTypes.bool,
  onAttributionEdit: PropTypes.func.isRequired,
  onColumnSettingsClick: PropTypes.func.isRequired,
  onExportPdf: PropTypes.func.isRequired,
  onIssueClick: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  onPhotosClick: PropTypes.func.isRequired,
  onShareClick: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onStatusEdit: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  pagy: PropTypes.shape({
    count: PropTypes.number,
    items: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number
  }),
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  sort: PropTypes.string,
  visibleColumns: PropTypes.shape({
    extra: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    })),
    primary: PropTypes.arrayOf(PropTypes.string)
  })
}

TriggeredAlertsTable.defaultProps = {
  activeIssueId: null,
  allFiltered: false,
  columnOrder: PRIMARY_COLUMN_ORDER,
  direction: 'desc',
  error: null,
  isIssueUpdating: null,
  issues: DEFAULT_ISSUES,
  loading: false,
  pagy: null,
  sort: null,
  visibleColumns: DEFAULT_VISIBLE_COLUMNS
}

export default TriggeredAlertsTable
